import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Avatar, Chip, Divider } from '@mui/material';
import { Heart, Carrot, Palette } from '@phosphor-icons/react';
import useThemeStyles from '../../../../theme/useThemeStyles';

const CoupleSection = ({ partners }) => {
  const styles = useThemeStyles();

  const getPartnerInitials = (partner) => {
    if (!partner?.fullName) return '?';
    const names = partner.fullName.split(' ');
    return names.map(name => name[0]).join('').toUpperCase();
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return 'Not specified';
    try {
      const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const renderPartnerCard = (partner, title) => {
    if (!partner || !partner.fullName) {
      return (
        <Card sx={{ height: '100%', ...styles.card }}>
          <CardContent>
            <Typography variant="body2" sx={{ ...styles.text.secondary, textAlign: 'center', py: 4 }}>
              {title} information not available
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{ height: '100%', ...styles.card }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                mr: 2,
                bgcolor: 'primary.main',
                ...styles.avatar
              }}
              src={partner.profilePicture}
            >
              {getPartnerInitials(partner)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, ...styles.text.primary }}>
                {partner.fullName}
              </Typography>
              {partner.preferredName && partner.preferredName !== partner.fullName && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Preferred: {partner.preferredName}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Contact Information */}
          {(partner.email || partner.phone || partner.address) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Contact Information
              </Typography>
              {partner.email && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Email:</strong> {partner.email}
                </Typography>
              )}
              {partner.phone && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Phone:</strong> {partner.phone}
                </Typography>
              )}
              {partner.address && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Address:</strong> {partner.address}
                </Typography>
              )}
            </Box>
          )}

          {/* Pronouns */}
          {partner.pronouns && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Pronouns
              </Typography>
              <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                {partner.pronouns}
              </Typography>
            </Box>
          )}

          {/* Wedding Role */}
          {(partner.planningRole || partner.mainWeddingDayContact) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Wedding Role
              </Typography>
              {partner.planningRole && (
                <Chip
                  label={partner.planningRole}
                  size="small"
                  sx={{ mb: 1, ...styles.chip }}
                />
              )}
              {partner.mainWeddingDayContact && (
                <Chip
                  label="Main Wedding Day Contact"
                  size="small"
                  color="primary"
                  sx={{ ml: 1, ...styles.chip }}
                />
              )}
            </Box>
          )}

          {/* Dietary & Accessibility */}
          {(partner.dietaryPreferences || partner.allergies || partner.accessibilityNeeds) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Dietary & Accessibility
              </Typography>
              {partner.dietaryPreferences && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Carrot size={16} style={{ marginRight: 8, color: '#64748b' }} />
                  <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                    {partner.dietaryPreferences}
                  </Typography>
                </Box>
              )}
              {partner.allergies && (
                <Typography variant="body2" sx={{ ...styles.text.secondary, color: 'warning.main' }}>
                  <strong>Allergies:</strong> {partner.allergies}
                </Typography>
              )}
              {partner.accessibilityNeeds && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Accessibility:</strong> {partner.accessibilityNeeds}
                </Typography>
              )}
            </Box>
          )}

          {/* Cultural & Religious */}
          {(partner.ceremonyType || partner.culturalNotes) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Cultural & Religious Considerations
              </Typography>
              {partner.ceremonyType && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Ceremony Type:</strong> {partner.ceremonyType}
                </Typography>
              )}
              {partner.culturalNotes && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Cultural Notes:</strong> {partner.culturalNotes}
                </Typography>
              )}
            </Box>
          )}

          {/* Personal Touches */}
          {(partner.styleNotes || partner.hobbies || partner.supplierNotes) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Personal Touches
              </Typography>
              {partner.styleNotes && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Palette size={16} style={{ marginRight: 8, color: '#64748b' }} />
                  <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                    <strong>Style:</strong> {partner.styleNotes}
                  </Typography>
                </Box>
              )}
              {partner.hobbies && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Hobbies:</strong> {partner.hobbies}
                </Typography>
              )}
              {partner.supplierNotes && (
                <Typography variant="body2" sx={{ ...styles.text.secondary, fontStyle: 'italic' }}>
                  <strong>Note for suppliers:</strong> {partner.supplierNotes}
                </Typography>
              )}
            </Box>
          )}

          {/* Optional Extras */}
          {(partner.proposalDate || partner.plannedWeddingDate || partner.newSurname) && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Optional Extras
              </Typography>
              {partner.proposalDate && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Proposal Date:</strong> {formatDate(partner.proposalDate)}
                </Typography>
              )}
              {partner.plannedWeddingDate && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>Planned Wedding Date:</strong> {formatDate(partner.plannedWeddingDate)}
                </Typography>
              )}
              {partner.newSurname && (
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  <strong>New Surname:</strong> {partner.newSurname}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Heart size={24} style={{ marginRight: 12, color: '#e91e63' }} />
        <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
          Couple Information
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          {renderPartnerCard(partners?.partnerOne, 'Partner One')}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderPartnerCard(partners?.partnerTwo, 'Partner Two')}
        </Grid>
      </Grid>
    </Box>
  );
};

export default CoupleSection;

